/**
 * AuthContext — tiny auth store backed by localStorage.
 *
 * Keeps a `user` object { username, role, name } plus a token. Persists to
 * localStorage so a refresh does not kick you out. Login hits the real
 * /api/auth/login endpoint; logout just clears state.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { apiCall } from '@/lib/api'

const AuthContext = createContext(null)
const STORAGE_KEY = 'expogreens.auth'

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (auth) localStorage.setItem(STORAGE_KEY, JSON.stringify(auth))
    else localStorage.removeItem(STORAGE_KEY)
  }, [auth])

  const login = useCallback(async (username, password) => {
    const res = await apiCall('/auth/login', {
      method: 'POST',
      body: { username, password },
    })
    setAuth(res)
    return res
  }, [])

  const logout = useCallback(() => setAuth(null), [])

  const value = useMemo(
    () => ({
      user: auth?.user || null,
      token: auth?.token || null,
      isAuthenticated: Boolean(auth?.user),
      login,
      logout,
    }),
    [auth, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
